<template>
    <div
        ref="dropdownRef"
        class="px-2 rounded-lg hover:bg-[#44587452] py-2 relative bg-[#1c2532] flex items-center justify-between"
        :class="[errorStyle, disabled && 'disabled']"
    >
        <!-- Select Box -->
        <div
            class="flex gap-1 w-full justify-between ellipsis items-center cursor-pointer"
            @click="selectClick"
        >
            <!-- 假如有插槽則用header插槽 -->
            <slot v-if="$slots.header"
                name="header"
            />
            <div v-else
                class="flex flex-col flex-[9] ellipsis justify-between"
                :class="{
                    'h-[40px]': props.label,
                    'slot-class': $slots.prevIcon, 
                }"
            >
                <slot v-if="$slots.prevIcon" name="prevIcon" />
                <span class="text-[#55657e] font-bold text-xs">
                    {{ label }}
                </span>
                <p class="flex flex-col justify-between">
                    <span v-if="prevLabel" class="text-[#55657e] font-bold text-xs">
                        {{ prevLabel }}
                    </span>
                    <span class="text-white ellipsis text-xs font-bold main-title">
                        {{ inerName }}
                    </span>
                </p>
                <slot v-if="$slots.subLabel" name="subLabel" />
            </div>
            <IconSvgUse
                use="icon-arrow-down-small"
                class="text-white w-6 h-6 duration-100"
                :class="[showPanel ? 'rotate-180' : '']"
            />
        </div>

        <div v-if="props.error" class="" :class="InputErrorStyle">
            {{ props.errorMessage }}
        </div>

        <div
            v-if="showPanel"
            class="absolute rounded-xl px-2 py-2 inset-x-0 bg-[#1c2532] z-10"
            :class="{
                [direction]: !!direction,
                'shadow': !!shadow,
            }"
        >
            <!-- Search Input -->
            <Input
                v-if="enableSearch"
                :input-class="'text-white'"
                containerCalss="h-[58px]  border flex text-white items-center bg-[#090c1199] px-2  border-[#2a3546] rounded-md w-full"
                v-model="search"
            >
                <template v-slot:prepend>
                    <QIcon
                        size="24px"
                        name="search"
                        class="flex w-[24px] h-[24px] text-[#55657e] mr-2"
                    ></QIcon>
                </template>
            </Input>
            <!-- Options -->
            <ul
                class="max-h-[200px] overflow-auto overflow-x-hidden flex mt-2 dropdown-list"
                :class="[optionsClass]"
            >
                <slot v-if="$slots.default" class="dropdown-item" />
                <DropDownItem
                    v-else
                    :current-value="String(modelValue)"
                    :value="String(option.value)"
                    @click="() => onOptionClick(option)"
                    v-for="option in inerOptions"
                    :key="option.name"
                    class="dropdown-item"
                >
                    <span v-if="showOptionIcon && option.optionIcon"
                        v-html="option.optionIcon"
                    ></span>
                    <IconSvgUse v-else-if="showOptionIcon && !option.optionIcon"
                        :use="`icon-${option.value}`"
                        class="w-[20px] text-[#93ACD3FF] h-[20px]"
                    />
                    <span class="truncate">
                        {{ option.name }}
                    </span>
                </DropDownItem>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { errorStyle as InputErrorStyle } from '~/config/style';
enum PanelDirection {
    TOP = 'top',
    BOTTOM = 'bottom',
}

interface OptionsType {
    id?: number;
    name: string;
    sort?: number;
    status?: number;
    title?: string;
    value: string;
    optionIcon?: string
}

const props = defineProps({
    options: { // select options
        type: Array as PropType<OptionsType[]>,
        required: true,
    },
    modelValue: { // 当前选中的value
        type: [Number, String],
        required: true,
        default: '',
    },
    panelDirection: {
        type: String,
        default: 'bottom',
    },
    label: { // 左右结构的label
        type: String,
        default: '',
    },
    error: { // 是否开启错误提醒
        type: Boolean,
        default: false,
    },
    errorMessage: { // 错误信息
        type: String,
        default: '',
    },
    enableSearch: { // 是否可以搜索
        type: Boolean,
        default: false,
    },
    optionsClass: {
        type: String,
        default: '',
    },
    showOptionIcon: { // 是否显示每一项的icon
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    prevLabel: { // 与name为上下结构的label
        type: String,
        default: '',
    },
    shadow: {
        type: Boolean,
    },
});
const toRefModalValue = toRef(props, 'modelValue');

const emit = defineEmits(['update:modelValue', 'update:showPanel ']);

const showPanel = ref(false);
const dropdownRef = ref();
const search = ref('');
const inerName = computed(() => {
    const currentValue = props.modelValue;
    if ([void 0, null].includes(currentValue)) {
        console.warn('选中值异常：', currentValue);
        return '';
    }
    const oTarget = props.options.find((option) => option.value == currentValue);
    if (!oTarget?.name){
        console.warn('<DropDown 未找到目标');
    }
    return oTarget?.name || '';
});

const inerOptions = computed(() => {
    if (props.enableSearch){
        return props.options.filter((option) =>
            option.name.toLowerCase().includes(search.value.toLowerCase()),
        );
    } else {
        return props.options
    }
});
const direction = computed(() => {
    if (props.panelDirection === PanelDirection.TOP) {
        return 'bottom-[120%]';
    }
    return 'top-[120%]';
});
// 错误信息样式
const errorStyle = computed(() => {
    if (props.error) {
        return 'border border-[#ff0000]';
    }
    return '';
});
/**
 * 点击选中option
 * @param option 
 */
const onOptionClick = (option: any) => {
    emit('update:modelValue', option.value);
    showPanel.value = false;
};
/**
 * 展开下拉选项
 */
const selectClick = () => {
    if (props.disabled) return;
    showPanel.value = !showPanel.value;
    emit('update:showPanel ', showPanel.value);
};
/**
 * 点击外部区域关闭下拉选项
 */
const closePanelOnClickOutside = (event: MouseEvent) => {
    if (showPanel.value && dropdownRef.value) {
        if (!dropdownRef.value.contains(event.target as Node)) {
            showPanel.value = false;
        }
    }
};
watch(toRefModalValue, () => {
    showPanel.value = false;
});
// 监听下拉列表展开，滚动到当前选中的option
watch(() => showPanel.value, (newVal) => {
    if (newVal){
        nextTick(() => {
            const { options, modelValue } = props;
            const dropdownList = document.querySelector('.dropdown-list');
            const dropdownItems = document.querySelectorAll('.dropdown-item');
            const index = options.findIndex(item => item.value === modelValue);
            const currentDom = dropdownItems[index];
            if (currentDom){
                dropdownList.scrollTo({
                    top: currentDom.scrollHeight * index,
                    left: 0
                })
            }
        })        
    }
})
onMounted(() => {
    document.addEventListener('click', closePanelOnClickOutside);
});
onUnmounted(() => {
    document.removeEventListener('click', closePanelOnClickOutside);
});
</script>

<style lang="scss" scoped>
.slot-class {
    gap: 2px;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start; // 已从 start 改为 flex-start 否则终端打印报错 
    flex-wrap: nowrap;
    .main-title {
        margin-right: 4px;
        max-width: 110px;
        text-align: left;
        width: 100%;
    }
}
.disabled {
    opacity: .4 !important;
}
.shadow{
    box-shadow: 2px 5px 6px #000000c9;
}
</style>

